import { APP_VERSION } from "@/env.mapper";
import { IUserAlternativeTemplate } from "@/types/event-template-alternative-user";
import { IGuestsData } from "@/types/guest";
import { cloudStorage } from "@tma.js/sdk-react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface AlternativeUserContextType {
  user: IUserAlternativeTemplate | null;
  setUser: (user: IUserAlternativeTemplate | null) => void;
  updateTags: (tags: IGuestsData["guestTag"][]) => void;
}

const AlternativeUserContext = createContext<
  AlternativeUserContextType | undefined
>(undefined);

export function AlternativeUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<IUserAlternativeTemplate | null>(null);
  useEffect(() => {
    const checkAppVersion = async () => {
      const prevVersion = localStorage.getItem("appVersion");

      if (APP_VERSION !== prevVersion) {
        // Clear Telegram CloudStorage cache
        try {
          if (cloudStorage.isSupported()) {
            const keys = await cloudStorage.getKeys();
            if (keys && keys.length > 0) {
              await Promise.all(
                keys.map((key) => cloudStorage.deleteItem(key)),
              );
            }
          }
        } catch (error) {
          console.error("Failed to clear CloudStorage:", error);
        }

        // Clear user state to force re-verification
        setUser(null);

        // Save version before showing popup
        localStorage.setItem("appVersion", APP_VERSION);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("v", APP_VERSION);
        currentUrl.searchParams.set("_t", Date.now().toString());
        window.location.href = currentUrl.toString();
      }
    };
    checkAppVersion();
  }, []);

  const updateTags = (tags: IGuestsData["guestTag"][]) => {
    if (user) {
      setUser({ ...user, guestTags: tags });
    }
  };

  return (
    <AlternativeUserContext.Provider value={{ user, setUser, updateTags }}>
      {children}
    </AlternativeUserContext.Provider>
  );
}

export function useAlternativeUser() {
  const context = useContext(AlternativeUserContext);
  if (context === undefined) {
    throw new Error(
      "useAlternativeUser must be used within an AlternativeUserProvider",
    );
  }
  return context;
}
